import { ca } from 'date-fns/locale';

type tPath = `/${string}`;

function createRoute<T>(
  basePath: string,
  path: tPath
): {
  path: tPath;
  to: (args?: T) => tPath;
} {
  const fullPath = `${basePath}${path}` as tPath;
  return {
    path: fullPath,
    to: (args) => {
      if (!args) return fullPath;
      return Object.entries(args).reduce(
        (acc, [key, value]) => acc.replace(`:${key}`, value as string) as tPath,
        fullPath
      );
    },
  };
}

const BASE_PATH = '/' + window.location.pathname.split('/')[1]; // You can dynamically set this

const routes = {
  newEvent: createRoute(BASE_PATH, '/events/new-event'),
  formInformation: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/form'
  ),
  inviteEvent: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/invite'
  ),
  editEvent: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/edit'
  ),
  offers: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/offers'
  ),
  admEventList: createRoute(BASE_PATH, '/events'),
  company: createRoute(BASE_PATH, '/company-setting'),
  eventlist: createRoute(BASE_PATH, '/eventlist'),
  home: createRoute(BASE_PATH, '/exhibitor'),
  home2: createRoute(BASE_PATH, '/'),
  login: createRoute(BASE_PATH, '/login'),
  signup: createRoute(BASE_PATH, '/signup'),
  forgotPassword: createRoute(BASE_PATH, '/forgot-password'),
  mainExhibitor: createRoute(BASE_PATH, '/exhibitor'),
  exhibitorID: createRoute(BASE_PATH, '/exhibitor/:exhibitorId'),
  event: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId'
  ),
  eventExhibitors: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/exhibitor-list'
  ),
  connectorAdmin: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/connector-admin'
  ),
  connectorAdminAdd: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/connector-admin/add'
  ),
  connectorModeratorAdd: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/connector-admin/add-moderator'
  ),
  editorPage: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/editor'
  ),
  editorPage2: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/editor-2'
  ),
  editorPage3: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/editor-3'
  ),
  editorPage4: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/editor-4'
  ),
  floorMap: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/floormap'
  ),
  eventForms: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/forms'
  ),
  eventFormDetail: createRoute<{
    eventId: string | number;
    formId: string | number;
  }>(BASE_PATH, '/events/:eventId/forms/:formId'),
  eventExhibitor: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/exhibitor/:exhibitorId'
  ),
  exhibitor: createRoute<{ exhibitorId: string | number }>(
    '',
    '/exhibitor/:exhibitorId'
  ),
  users: createRoute(BASE_PATH, '/users'),
  newUser: createRoute(BASE_PATH, '/users/new-user'),
  userEdit: createRoute<{ userId: string | number }>(
    BASE_PATH,
    '/users/:userId'
  ),
  companyAdd: createRoute<{ userId: string | number }>(
    BASE_PATH,
    '/users/:userId/new-company'
  ),
  categories: createRoute(BASE_PATH, '/categories'),
  newCategory: createRoute(BASE_PATH, '/categories/new-category'),
  categoryEdit: createRoute<{ categoryId: string | number }>(
    BASE_PATH,
    '/categories/:categoryId'
  ),
  universes: createRoute(BASE_PATH, '/universes'),
  newUniverse: createRoute(BASE_PATH, '/universes/new-universe'),
  universeEdit: createRoute<{ universeId: string | number }>(
    BASE_PATH,
    '/universes/:categoryId'
  ),
  subcategories: createRoute(BASE_PATH, '/subcategories'),
  newSubcategory: createRoute(BASE_PATH, '/subcategories/new-subcategory'),
  subcategoryEdit: createRoute<{ subcategoryId: string | number }>(
    BASE_PATH,
    '/subcategories/:categoryId'
  ),

  adminDashboard: createRoute(BASE_PATH, '/admin-dashboard'),
  profile: createRoute(BASE_PATH, '/users'),
  newProfile: createRoute(BASE_PATH, '/users/new-user'),
  profileEdit: createRoute<{ userId: string | number }>(
    BASE_PATH,
    '/users/:userId'
  ),
  product: createRoute(BASE_PATH, '/product'),
  products: createRoute(BASE_PATH, '/products'),
  qrProductId: createRoute<{ productId: string | number }>(
    BASE_PATH,
    '/products/:productId'
  ),
  productId: createRoute<{ productId: string | number }>(
    BASE_PATH,
    '/product/:productId'
  ),
  productAdmin: createRoute<{ exhibitorId?: string | number }>(
    '',
    '/exhibitor/:exhibitorId/product'
  ),
  productIdAdmin: createRoute<{
    exhibitorId?: string | number;
    productId: string | number;
  }>(BASE_PATH, '/exhibitor/:exhibitorId/product/:productId'),
  codif: createRoute(BASE_PATH, '/codif'),
  confirmation: createRoute<{ confirmationId: string }>(
    BASE_PATH,
    '/confirmation/:confirmationId'
  ),
  updatePassword: createRoute<{ updatePasswordId: string }>(
    BASE_PATH,
    '/update-password/:updatePasswordId'
  ),
  acceptInvite: createRoute(BASE_PATH, '/accept/:code'),
  eventRegistration: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/eventlist/event-registration/:exhibitorEventId'
  ),
  editProductEventRegistrationPage: createRoute(
    BASE_PATH,
    '/eventlist/event-registration/:exhibitorEventId/product/:productId'
  ),
  publishedEventPage: createRoute<{ slug: string }>(
    BASE_PATH,
    '/published/:slug'
  ),
  publicEvents: createRoute(BASE_PATH, '/preview/events'),
  publicEventPages: createRoute<{ slug: string | number }>(
    BASE_PATH,
    '/preview/events/:slug/pages'
  ),
  publicEventExhibitors: createRoute<{ slug: string | number }>(
    BASE_PATH,
    '/preview/events/:slug/exhibitors'
  ),
  publicEventProducts: createRoute<{ slug: string | number }>(
    BASE_PATH,
    '/preview/events/:slug/products'
  ),
  publicEventProductDetail: createRoute<{ slug: string | number }>(
    BASE_PATH,
    '/preview/events/:slug/products/:product'
  ),
  publicEventExhibitorDetail: createRoute<{ slug: string | number }>(
    BASE_PATH,
    '/preview/events/:slug/exhibitors/:eid'
  ),
  notFound: createRoute(BASE_PATH, '/not-found'),

  // Matchmaking (bbmatch shown inside EM)
  matchmaking: createRoute(BASE_PATH, '/matchmaking'),
  eventMatchmaking: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/matchmaking'
  ),

  // Ticketing
  ticketingSetup: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/ticketing'
  ),
  ticketingTickets: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/ticketing/tickets'
  ),
  ticketingStats: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/ticketing/stats'
  ),
  ticketingCheckpoints: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/ticketing/checkpoints'
  ),
  ticketingExhibitors: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/events/:eventId/ticketing/exhibitors'
  ),
  exhibitorBadges: createRoute<{ code: string }>(BASE_PATH, '/badges/:code'),
  scan: createRoute(BASE_PATH, '/scan'),
  scanCheckpoint: createRoute<{ token: string }>(BASE_PATH, '/scan/p/:token'),
  scanEvent: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/scan/:eventId'
  ),
  publicTickets: createRoute<{ slug: string }>(BASE_PATH, '/tickets/:slug'),
  publicTicketOrder: createRoute<{ slug: string; reference: string }>(
    BASE_PATH,
    '/tickets/:slug/order/:reference'
  ),

  // CRM
  crmDashboard: createRoute(BASE_PATH, '/crm'),
  crmPipeline: createRoute(BASE_PATH, '/crm/pipeline'),
  crm: createRoute(BASE_PATH, '/crm/prospects'),
  crmCompanies: createRoute(BASE_PATH, '/crm/companies'),
  crmCompanyNew: createRoute(BASE_PATH, '/crm/companies/new'),
  crmCompanyEdit: createRoute<{ companyId: string | number }>(
    BASE_PATH,
    '/crm/companies/:companyId/edit'
  ),
  crmCompanyDetail: createRoute<{ companyId: string | number }>(
    BASE_PATH,
    '/crm/companies/:companyId'
  ),
  crmProspects: createRoute<{ eventId: string | number }>(
    BASE_PATH,
    '/crm/events/:eventId/prospects'
  ),
  crmProspectNew: createRoute(BASE_PATH, '/crm/prospects/new'),
  crmProspectEdit: createRoute<{ prospectId: string | number }>(
    BASE_PATH,
    '/crm/prospects/:prospectId/edit'
  ),
  crmProspectDetail: createRoute<{ prospectId: string | number }>(
    BASE_PATH,
    '/crm/prospects/:prospectId'
  ),
};

export default routes;
